import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { currency, num, dateTime, dateShort } from "@/lib/format";
import { StatusBadge, CategoryBadge, SkuTag } from "@/components/Badges";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Boxes, Package, DollarSign, Warehouse } from "lucide-react";

const Stat = ({ label, value, tone }) => (
  <div className="rounded-lg border border-stone-200 bg-white p-4">
    <div className="text-xs uppercase tracking-wide text-stone-400">{label}</div>
    <div className={`text-xl font-extrabold font-mono mt-1 ${tone || "text-stone-900"}`}>{value}</div>
  </div>
);

export default function ItemDetail() {
  const { code } = useParams();
  const [s, setS] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get(`/skus/${code}`).then((r) => setS(r.data)).catch(() => setS(false)); }, [code]);

  if (s === false) return <div className="p-8 text-stone-400">Item not found.</div>;
  if (!s) return <div className="p-8 text-stone-400">Loading…</div>;

  const pct = s.reorder_point ? Math.min(100, (s.total_on_hand / (s.reorder_point * 2)) * 100) : 100;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-6">
      <button data-testid="back-btn" onClick={() => navigate("/skus")} className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-900 transition-colors">
        <ArrowLeft className="h-4 w-4" />Back to SKUs
      </button>

      <div className="rounded-xl bg-white border border-stone-200/80 shadow-sm p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2"><SkuTag code={s.sku_code} /><CategoryBadge category={s.category} /></div>
            <h1 className="text-3xl font-bold tracking-tight text-stone-900">{s.name}</h1>
            <p className="text-sm text-stone-500 mt-1">Vendor: {s.vendor || "—"}{s.notes ? ` · ${s.notes}` : ""}</p>
          </div>
          <StatusBadge status={s.status} />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
          <Stat label="On Hand" value={`${num(s.total_on_hand)} ${s.unit}`} />
          <Stat label="Reorder Point" value={num(s.reorder_point)} />
          <Stat label="Unit Cost" value={currency(s.unit_cost)} />
          <Stat label="Total Value" value={currency(s.total_value)} tone="text-amber-800" />
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between text-xs text-stone-500 mb-1.5">
            <span>Stock vs reorder point</span><span className="font-mono">{num(s.total_on_hand)} / {num(s.reorder_point)}</span>
          </div>
          <div className="h-2.5 rounded-full bg-stone-100 overflow-hidden relative">
            <div className="absolute top-0 bottom-0 w-px bg-stone-400" style={{ left: "50%" }} />
            <div className={`h-full rounded-full ${s.status === "out_of_stock" ? "bg-rose-500" : s.status === "low_stock" ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>

      <Tabs defaultValue="stock">
        <TabsList className="bg-stone-100">
          <TabsTrigger value="stock" data-testid="tab-stock"><Warehouse className="h-4 w-4 mr-1.5" />Locations</TabsTrigger>
          <TabsTrigger value="where" data-testid="tab-where"><Boxes className="h-4 w-4 mr-1.5" />Where Used</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger"><Package className="h-4 w-4 mr-1.5" />Movements</TabsTrigger>
          <TabsTrigger value="adj" data-testid="tab-adj"><DollarSign className="h-4 w-4 mr-1.5" />Adjustments</TabsTrigger>
        </TabsList>

        <TabsContent value="stock">
          <div className="rounded-lg border border-stone-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="text-xs font-semibold uppercase tracking-wider text-stone-500 bg-stone-50/80 border-b border-stone-200">
                <th className="px-4 py-3 text-left">Facility</th><th className="px-4 py-3 text-right">Qty</th>
                <th className="px-4 py-3 text-right">Value</th><th className="px-4 py-3 text-left">Status</th></tr></thead>
              <tbody>
                {s.breakdown.map((b) => (
                  <tr key={b.facility_id} className="border-b border-stone-100 last:border-0 hover:bg-stone-50/70">
                    <td className="px-4 py-3"><Link to={`/facilities/${b.facility_id}`} className="font-medium text-stone-800 hover:underline">{b.facility_name}</Link></td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums">{num(b.qty)} {s.unit}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums font-semibold">{currency(b.value)}</td>
                    <td className="px-4 py-3"><StatusBadge status={b.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="where">
          <div className="rounded-lg border border-stone-200 bg-white overflow-hidden">
            {s.where_used.length === 0 ? <div className="text-center py-12 text-stone-400">Not used in any BOM.</div> : (
              <table className="w-full text-sm">
                <thead><tr className="text-xs font-semibold uppercase tracking-wider text-stone-500 bg-stone-50/80 border-b border-stone-200">
                  <th className="px-4 py-3 text-left">BOM</th><th className="px-4 py-3 text-left">Target SKU</th><th className="px-4 py-3 text-right">Qty per Batch</th></tr></thead>
                <tbody>
                  {s.where_used.map((w) => (
                    <tr key={w.bom_code} className="border-b border-stone-100 last:border-0 hover:bg-stone-50/70">
                      <td className="px-4 py-3"><SkuTag code={w.bom_code} /></td>
                      <td className="px-4 py-3"><SkuTag code={w.target_sku} /></td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums">{num(w.qty)} {w.unit || s.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="ledger">
          <div className="rounded-lg border border-stone-200 bg-white overflow-hidden">
            {s.movements.length === 0 ? <div className="text-center py-12 text-stone-400">No movements recorded.</div> : (
              <table className="w-full text-sm">
                <thead><tr className="text-xs font-semibold uppercase tracking-wider text-stone-500 bg-stone-50/80 border-b border-stone-200">
                  <th className="px-4 py-3 text-left">Date</th><th className="px-4 py-3 text-left">Type</th><th className="px-4 py-3 text-left">Facility</th>
                  <th className="px-4 py-3 text-right">Delta</th><th className="px-4 py-3 text-left">Ref</th><th className="px-4 py-3 text-left">Note</th></tr></thead>
                <tbody>
                  {s.movements.map((m, i) => (
                    <tr key={i} className="border-b border-stone-100 last:border-0 hover:bg-stone-50/70">
                      <td className="px-4 py-3 text-stone-600">{dateTime(m.date)}</td>
                      <td className="px-4 py-3 capitalize text-stone-600">{m.type}</td>
                      <td className="px-4 py-3 text-stone-600">{m.facility}</td>
                      <td className={`px-4 py-3 text-right font-mono tabular-nums font-semibold ${m.delta >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{m.delta >= 0 ? "+" : ""}{num(m.delta)}</td>
                      <td className="px-4 py-3"><SkuTag code={m.ref} /></td>
                      <td className="px-4 py-3 text-stone-500 text-xs">{m.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="adj">
          <div className="rounded-lg border border-stone-200 bg-white overflow-hidden">
            {s.adjustments.length === 0 ? <div className="text-center py-12 text-stone-400">No adjustments recorded.</div> : (
              <table className="w-full text-sm">
                <thead><tr className="text-xs font-semibold uppercase tracking-wider text-stone-500 bg-stone-50/80 border-b border-stone-200">
                  <th className="px-4 py-3 text-left">ID</th><th className="px-4 py-3 text-left">Date</th><th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-right">Delta</th><th className="px-4 py-3 text-left">Reason</th></tr></thead>
                <tbody>
                  {s.adjustments.map((a) => (
                    <tr key={a.adj_no} className="border-b border-stone-100 last:border-0 hover:bg-stone-50/70">
                      <td className="px-4 py-3"><SkuTag code={a.adj_no} /></td>
                      <td className="px-4 py-3 text-stone-600">{dateShort(a.date)}</td>
                      <td className="px-4 py-3 text-stone-600">{a.type}</td>
                      <td className={`px-4 py-3 text-right font-mono tabular-nums font-semibold ${a.delta_qty >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{a.delta_qty >= 0 ? "+" : ""}{num(a.delta_qty)}</td>
                      <td className="px-4 py-3 text-stone-500 text-xs">{a.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
